/* global Dropbox, OneDrive */
import { useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Cloud, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const DROPBOX_APP_KEY = process.env.REACT_APP_DROPBOX_APP_KEY || '';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_PICKER_CLIENT_ID || '';
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PICKER_API_KEY || '';
const MS_CLIENT_ID = process.env.REACT_APP_MS_CLIENT_ID || '';

const DROPBOX_SCRIPT = 'https://www.dropbox.com/static/api/2/dropins.js';
const ONEDRIVE_SCRIPT = 'https://js.live.net/v7.2/OneDrive.js';
const GAPI_SCRIPT = 'https://apis.google.com/js/api.js';
const GIS_SCRIPT = 'https://accounts.google.com/gsi/client';

const PROVIDER_LABEL = {
  dropbox: 'Dropbox',
  google_drive: 'Google Drive',
  onedrive: 'OneDrive / SharePoint',
};

/** Lazy-load a script tag exactly once; resolves when ready. */
function loadScript(src, attrs = {}) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
    s.async = true;
    s.onload = () => { s.dataset.loaded = '1'; resolve(); };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function CloudFilePicker({ onSaved }) {
  const { clientId } = useOutletContext();
  const { token } = useAuth();
  const [busy, setBusy] = useState(null);
  const gapiReady = useRef(false);

  const anyEnabled = !!(DROPBOX_APP_KEY || (GOOGLE_CLIENT_ID && GOOGLE_API_KEY) || MS_CLIENT_ID);

  // -------- Backend save --------
  const saveAsLink = async (file) => {
    try {
      await api.createLink(token, clientId, {
        title: file.name,
        sourceSystem: PROVIDER_LABEL[file.provider] || file.provider,
        category: 'External',
        owner: '',
        reviewCadence: 'Annually',
        status: 'Linked',
        lastVerified: new Date().toISOString().slice(0, 10),
        url: file.url,
        reason: `Linked from ${PROVIDER_LABEL[file.provider] || file.provider}${file.size ? ` (${(file.size / 1024).toFixed(1)} KB)` : ''}`,
      });
      toast.success(`Linked ${file.name} from ${PROVIDER_LABEL[file.provider]}`);
      onSaved?.();
    } catch (err) {
      console.error('Cloud picker save failed', err);
      toast.error(err?.response?.data?.detail || 'Could not save linked file');
    }
  };

  // -------- Dropbox --------
  const pickDropbox = async () => {
    if (!DROPBOX_APP_KEY) {
      toast.error('Dropbox not configured (REACT_APP_DROPBOX_APP_KEY missing)');
      return;
    }
    setBusy('dropbox');
    try {
      await loadScript(DROPBOX_SCRIPT, { id: 'dropboxjs', 'data-app-key': DROPBOX_APP_KEY });
      if (typeof Dropbox === 'undefined') throw new Error('Dropbox SDK not available');
      Dropbox.choose({
        linkType: 'preview',
        multiselect: false,
        success: (files) => {
          const f = files?.[0];
          if (!f) return;
          saveAsLink({
            provider: 'dropbox',
            name: f.name,
            url: f.link,
            size: f.bytes || 0,
          });
        },
        cancel: () => {},
      });
    } catch (err) {
      console.error('Dropbox picker failed', err);
      toast.error('Could not open Dropbox picker');
    } finally {
      setBusy(null);
    }
  };

  // -------- Google Drive --------
  const pickGoogleDrive = async () => {
    if (!GOOGLE_CLIENT_ID || !GOOGLE_API_KEY) {
      toast.error('Google Drive not configured (need REACT_APP_GOOGLE_PICKER_CLIENT_ID + API_KEY)');
      return;
    }
    setBusy('google_drive');
    try {
      await Promise.all([loadScript(GAPI_SCRIPT), loadScript(GIS_SCRIPT)]);
      if (!gapiReady.current) {
        await new Promise((resolve) => window.gapi.load('picker', { callback: resolve }));
        gapiReady.current = true;
      }
      // Ask user to sign in via Google Identity Services
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (resp) => {
          if (!resp.access_token) return;
          const picker = new window.google.picker.PickerBuilder()
            .addView(window.google.picker.ViewId.DOCS)
            .setOAuthToken(resp.access_token)
            .setDeveloperKey(GOOGLE_API_KEY)
            .setCallback((data) => {
              if (data.action === window.google.picker.Action.PICKED) {
                const f = data.docs?.[0];
                if (!f) return;
                saveAsLink({
                  provider: 'google_drive',
                  name: f.name,
                  url: f.url,
                  size: parseInt(f.sizeBytes || '0', 10) || 0,
                });
              }
            })
            .build();
          picker.setVisible(true);
        },
      });
      tokenClient.requestAccessToken({ prompt: '' });
    } catch (err) {
      console.error('Google Drive picker failed', err);
      toast.error('Could not open Google Drive picker');
    } finally {
      setBusy(null);
    }
  };

  // -------- OneDrive / SharePoint --------
  const pickOneDrive = async () => {
    if (!MS_CLIENT_ID) {
      toast.error('OneDrive not configured (REACT_APP_MS_CLIENT_ID missing)');
      return;
    }
    setBusy('onedrive');
    try {
      await loadScript(ONEDRIVE_SCRIPT);
      if (typeof OneDrive === 'undefined') throw new Error('OneDrive SDK not available');
      OneDrive.open({
        clientId: MS_CLIENT_ID,
        action: 'share',
        multiSelect: false,
        advanced: { redirectUri: window.location.origin },
        success: (resp) => {
          const f = resp?.value?.[0];
          if (!f) return;
          const url = f?.permissions?.[0]?.link?.webUrl || f.webUrl || '';
          saveAsLink({
            provider: 'onedrive',
            name: f.name,
            url,
            size: f.size || 0,
          });
        },
        cancel: () => {},
        error: (e) => {
          console.error('OneDrive picker error', e);
          toast.error('OneDrive picker error');
        },
      });
    } catch (err) {
      console.error('OneDrive picker failed', err);
      toast.error('Could not open OneDrive picker');
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 border-[#E6E0D9] text-[#2C2420] hover:bg-[#F9F7F5]"
          data-testid="cloud-picker-trigger"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
          Link from cloud
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" data-testid="cloud-picker-menu">
        <DropdownMenuLabel className="text-xs text-[#8C8078] font-normal">
          Pick a file from your cloud
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); pickDropbox(); }}
          disabled={!DROPBOX_APP_KEY || busy === 'dropbox'}
          data-testid="cloud-picker-dropbox"
        >
          <span className="flex-1">Dropbox</span>
          {!DROPBOX_APP_KEY && <AlertCircle className="w-3.5 h-3.5 text-amber-500" aria-label="Not configured" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); pickGoogleDrive(); }}
          disabled={!(GOOGLE_CLIENT_ID && GOOGLE_API_KEY) || busy === 'google_drive'}
          data-testid="cloud-picker-google"
        >
          <span className="flex-1">Google Drive</span>
          {!(GOOGLE_CLIENT_ID && GOOGLE_API_KEY) && <AlertCircle className="w-3.5 h-3.5 text-amber-500" aria-label="Not configured" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); pickOneDrive(); }}
          disabled={!MS_CLIENT_ID || busy === 'onedrive'}
          data-testid="cloud-picker-onedrive"
        >
          <span className="flex-1">OneDrive / SharePoint</span>
          {!MS_CLIENT_ID && <AlertCircle className="w-3.5 h-3.5 text-amber-500" aria-label="Not configured" />}
        </DropdownMenuItem>
        {!anyEnabled && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-[#8C8078]">
              No providers configured yet. Set the env vars to enable.
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
